<script setup lang="ts">
  // Components
  import {
    FilterModal,
    DatePicker,
    DateRangePicker,
    IconImage,
    ThemeProvider,
    SelectInput,
  } from '@/shared/ui';
  // Assets
  import CalendarIcon from '@/shared/assets/icons/CalendarIcon.svg';
  import ArrowRight from '@/shared/assets/icons/ArrowRight.svg';
  // Composables
  import { useFetchUsers } from '@/entities/User';
  import { useGenericFilterModal, type BaseFilterData } from '@/shared/lib';
  import { useFilterStore } from '@/entities/Filter';
  // Misc
  import {
    STATUS_LIST,
    STAGE_LIST,
    mapFiltersToFormData,
    mapFormDataToFilters,
  } from '@/entities/Document';
  // Vue
  import { computed, ref } from 'vue';
  import { useRoute } from 'vue-router';
  // Types
  import { DocumentRevisionStatus, DocumentType } from '@/shared/lib';

  const props = defineProps<{
    currentFilters?: BaseFilterData;
    documentType?: DocumentType;
  }>();

  const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'update:filter', filters: BaseFilterData): void;
    (e: 'update:selectedDate', date: Date | null): void;
    (e: 'clear-filters'): void;
  }>();

  const { fetchAllUsers } = useFetchUsers();
  const filterStore = useFilterStore();

  // Default form data for search filters
  const defaultSearchFormData: BaseFilterData = {
    revisionStatus: '',
    elaborationDate: null,
    requestDate: null,
    approvalDate: null,
    effectiveDate: null,
    validFrom: null,
    validUntil: null,
    revision: 0,
    elaborationApproval: '',
    branchId: 0,
    hasTraining: null,
    status: '',
    stage: '',
    distribution: '',
    format: '',
    validityDate: null,
    physicalLocation: '',
    isExternal: null,
    elaboratedBy: '',
    approvedBy: '',
    verifiedBy: '',
    verificationDate: null,
    documentType: '',
  };

  const {
    formData,
    elaborationDate,
    approvalDate,
    effectiveDate,
    isElaborationDateOpen,
    isApprovalDateOpen,
    isEffectiveDateOpen,
    formatDate,
    clearFilters,
    applyFilters: genericApplyFilters,
  } = useGenericFilterModal(
    emit,
    defaultSearchFormData,
    fetchAllUsers,
    (props.currentFilters || filterStore.filters) as Partial<BaseFilterData>,
    mapFiltersToFormData as (filters: any) => BaseFilterData
  );

  const applyFilters = () => {
    genericApplyFilters(mapFormDataToFilters);
  };

  const route = useRoute();

  const filteredStatusList = computed(() => {
    const statusList = [...STATUS_LIST];
    if (route.path === '/pesquisa') {
      // Match the statuses shown in Gestão de Documentos for Complete Search
      return statusList.filter(
        (status) =>
          status.value === DocumentRevisionStatus.CURRENT ||
          status.value === DocumentRevisionStatus.OBSOLETE ||
          status.value === DocumentRevisionStatus.INACTIVE
      );
    }
    // Show all statuses for other routes
    return statusList;
  });

  const statusOptions = computed(() => {
    const options = filteredStatusList.value.map((status) => ({
      id: status.value,
      name: status.label,
    }));

    if (route.path === '/pesquisa') {
      options.push({ id: 'revogado', name: 'Revogado' });
    }

    return options;
  });

  const stageOptions = computed(() =>
    [...STAGE_LIST].map((stage) => ({ id: stage.value, name: stage.label }))
  );

  const isValidRangeOpen = ref(false);

  const formatValidRange = () => {
    const from = formData.value.validFrom;
    const until = formData.value.validUntil;

    if (from && until) return `${formatDate(from)} - ${formatDate(until)}`;
    if (from) return formatDate(from);
    if (until) return formatDate(until);
    return 'Selecione uma data ou período';
  };

  const handleClearFilters = () => {
    clearFilters();
    emit('clear-filters');
  };

  defineExpose({ isValidRangeOpen });
</script>

<template>
  <FilterModal
    content-class="w-full max-w-sm"
    @close="emit('close')"
    @clear-filters="handleClearFilters"
    @apply-filters="applyFilters"
  >
    <template #content>
      <ThemeProvider class="overflow-auto">
        <div class="divide-y divide-gray-02">
          <div
            v-if="documentType !== DocumentType.DOCUMENT"
            class="p-6"
          >
            <h4 class="font-medium text-gray-07 pb-4">
              Filial
            </h4>
            <div class="flex flex-col gap-2">
              <button
                :class="[
                  'py-2 rounded-lg w-30',
                  formData.branchId === 1 ? 'bg-(--theme-secondary) text-white' : 'text-gray-07',
                ]"
                @click="formData.branchId = formData.branchId === 1 ? 0 : 1"
              >
                Matriz
              </button>
              <button
                :class="[
                  'py-2 rounded-lg w-30',
                  formData.branchId === 2 ? 'bg-(--theme-secondary) text-white' : 'text-gray-07',
                ]"
                @click="formData.branchId = formData.branchId === 2 ? 0 : 2"
              >
                Veterinária
              </button>
              <button
                :class="[
                  'py-2 rounded-lg w-30',
                  formData.branchId === 3 ? 'bg-(--theme-secondary) text-white' : 'text-gray-07',
                ]"
                @click="formData.branchId = formData.branchId === 3 ? 0 : 3"
              >
                Fisioterapia
              </button>
            </div>
          </div>

          <div class="p-6">
            <h4 class="font-medium text-gray-07 pb-4">
              Treinamento
            </h4>
            <div class="flex flex-col gap-2">
              <button
                :class="[
                  'py-2 rounded-lg w-40',
                  formData.hasTraining === true
                    ? 'bg-(--theme-secondary) text-white'
                    : 'text-gray-07',
                ]"
                @click="formData.hasTraining = formData.hasTraining === true ? null : true"
              >
                Com treinamento
              </button>
              <button
                :class="[
                  'py-2 rounded-lg w-40',
                  formData.hasTraining === false
                    ? 'bg-(--theme-secondary) text-white'
                    : 'text-gray-07',
                ]"
                @click="formData.hasTraining = formData.hasTraining === false ? null : false"
              >
                Sem treinamento
              </button>
            </div>
          </div>

          <div class="p-6">
            <h4 class="font-medium text-gray-07 mb-2">
              Status
            </h4>
            <SelectInput
              :model-value="formData.status ?? ''"
              :options="statusOptions"
              :use-teleport="true"
              label="Selecione um status"
              max-height="12rem"
              @update:model-value="formData.status = String($event)"
            />
          </div>

          <div
            v-if="documentType !== DocumentType.DOCUMENT"
            class="p-6"
          >
            <h4 class="font-medium text-gray-07 mb-2">
              Etapa
            </h4>
            <SelectInput
              :model-value="formData.stage ?? ''"
              :options="stageOptions"
              :use-teleport="true"
              label="Selecione uma etapa"
              max-height="12rem"
              @update:model-value="formData.stage = String($event)"
            />
          </div>

          <div
            v-if="documentType === DocumentType.DOCUMENT"
            class="p-6"
          >
            <h4 class="font-medium text-gray-07 pb-4">
              Data ou período
            </h4>
            <button
              class="flex items-center bg-white-01 px-4 py-3 border border-gray-02 rounded-lg text-gray-03 focus:outline-none focus:border-(--theme-secondary-ink) w-56"
              @click="isValidRangeOpen = true"
            >
              <IconImage
                :src="CalendarIcon"
                alt="Calendário"
                class="h-5 w-5 shrink-0"
              />
              <span class="text-sm ml-2 truncate">{{ formatValidRange() }}</span>
            </button>
            <DateRangePicker
              :start-date="formData.validFrom ?? null"
              :end-date="formData.validUntil ?? null"
              v-model:is-open="isValidRangeOpen"
              class="my-auto"
              @update:start-date="formData.validFrom = $event"
              @update:end-date="formData.validUntil = $event"
              @apply="isValidRangeOpen = false"
              @cancel="isValidRangeOpen = false"
            />
          </div>
          <template v-else>
            <div class="p-6">
              <h4 class="font-medium text-gray-07 pb-4">
                Data da Criação
              </h4>
              <button
                class="flex items-center bg-white-01 px-4 py-3 border border-gray-02 rounded-lg text-gray-03 focus:outline-none focus:border-(--theme-secondary-ink) w-56"
                @click="isElaborationDateOpen = true"
              >
                <IconImage
                  :src="CalendarIcon"
                  alt="Calendário"
                  class="h-5 w-5 shrink-0"
                />
                <span class="text-sm ml-2 truncate">{{
                  elaborationDate !== null ? formatDate(elaborationDate) : 'Selecione uma data'
                }}</span>
              </button>
              <DatePicker
                v-model:selected-date="elaborationDate"
                v-model:is-open="isElaborationDateOpen"
                class="my-auto"
                @apply="isElaborationDateOpen = false"
                @cancel="isElaborationDateOpen = false"
              />
            </div>

            <div class="p-6">
              <h4 class="font-medium text-gray-07 pb-4">
                Data da Vigência
              </h4>
              <button
                class="flex items-center bg-white-01 px-4 py-3 border border-gray-02 rounded-lg text-gray-03 focus:outline-none focus:border-(--theme-secondary-ink) w-56"
                @click="isEffectiveDateOpen = true"
              >
                <IconImage
                  :src="CalendarIcon"
                  alt="Calendário"
                  class="h-5 w-5 shrink-0"
                />
                <span class="text-sm ml-2 truncate">{{
                  effectiveDate !== null ? formatDate(effectiveDate) : 'Selecione uma data'
                }}</span>
              </button>
              <DatePicker
                v-model:selected-date="effectiveDate"
                v-model:is-open="isEffectiveDateOpen"
                class="my-auto"
                @apply="isEffectiveDateOpen = false"
                @cancel="isEffectiveDateOpen = false"
              />
            </div>

            <div class="p-6">
              <h4 class="font-medium text-gray-07 pb-4">
                Data de Conclusão
              </h4>
              <button
                class="flex items-center bg-white-01 px-4 py-3 border border-gray-02 rounded-lg text-gray-03 focus:outline-none focus:border-(--theme-secondary-ink) w-56"
                @click="isApprovalDateOpen = true"
              >
                <IconImage
                  :src="CalendarIcon"
                  alt="Calendário"
                  class="h-5 w-5 shrink-0"
                />
                <span class="text-sm ml-2 truncate">{{
                  approvalDate !== null ? formatDate(approvalDate) : 'Selecione uma data'
                }}</span>
              </button>
              <DatePicker
                v-model:selected-date="approvalDate"
                v-model:is-open="isApprovalDateOpen"
                class="my-auto"
                @apply="isApprovalDateOpen = false"
                @cancel="isApprovalDateOpen = false"
              />
            </div>
          </template>

          <div class="p-6">
            <h4 class="font-medium text-gray-07 mb-2">
              Revisões
            </h4>
            <div class="relative w-28">
              <input
                v-model.number="formData.revision"
                type="number"
                class="w-full px-4 py-3 rounded-lg border border-gray-02 text-gray-03 [appearance:textfield] [-webkit-appearance:textfield] [-moz-appearance:textfield] [&::-webkit-outer-spin-button]:m-0 [&::-webkit-inner-spin-button]:m-0 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                min="0"
              >
              <div class="absolute right-3 top-1/2 -translate-y-1/2 flex flex-col">
                <IconImage
                  :src="ArrowRight"
                  alt="Aumentar"
                  class="h-6 w-6 rotate-270 cursor-pointer -ml-[1.52px]"
                  @click="formData.revision = (formData.revision ?? 0) + 1"
                />
                <IconImage
                  :src="ArrowRight"
                  alt="Diminuir"
                  class="h-6 w-6 rotate-90 cursor-pointer"
                  @click="
                    formData.revision =
                      (formData.revision ?? 0) > 0 ? (formData.revision ?? 0) - 1 : 0
                  "
                />
              </div>
            </div>
          </div>
        </div>
      </ThemeProvider>
    </template>
  </FilterModal>
</template>
